import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { Link } from "@shapr3d/frontend-lib";
import classNames from "classnames";
import { createPortal } from "react-dom";

import { useScript } from "../../hooks/useScript";

import style from "./WistiaVideo.module.css";

export interface WistiaAPI {
  play: () => void;
  remove: () => void;
  bind: (arg1: string, arg2: () => void) => void;
  unbind: (arg1: string, arg2: () => void) => void;
  time: (arg1?: number) => number;
  height: (arg1?: number, arg2?: { constrain: boolean }) => number;
  duration: () => number;
  popover: {
    show: () => void;
    hide: () => void;
  };
}

declare global {
  interface Window {
    _wq: Array<{ id: string; onReady: (video: WistiaAPI) => void }>;
    //eslint-disable-next-line
    Wistia: any;
  }
}

interface WistiaVideoProps {
  videoID: string;
  onLoad?: (video: WistiaAPI) => void;
  children: React.ReactNode;
}

type WistiaVideoContext = {
  video: WistiaAPI | null;
  isLoaded: boolean;
  captionContainerID: string;
};

export const WistiaVideoContext = React.createContext<WistiaVideoContext>({
  video: null,
  isLoaded: false,
  captionContainerID: "",
} satisfies WistiaVideoContext);

const InternalWistiaVideo = forwardRef<React.MutableRefObject<WistiaAPI | null>, WistiaVideoProps>(
  ({ videoID, children, onLoad }, _ref) => {
    useScript({
      url: "https://fast.wistia.com/assets/external/E-v1.js",
      async: true,
      id: "wistia",
    });

    //TODO This is no longer good enough for testing, need to come up with a better solution
    //const internalVideoID = environment === Environment.PROD ? videoID : placeholderWistiaVideoID;
    const internalVideoID = videoID;

    const [isLoaded, setIsLoaded] = useState(false);
    const videoHandleRef = useRef<WistiaAPI | null>(null);
    const captionContainerID = `caption-container-${internalVideoID}`;

    useImperativeHandle(_ref, () => videoHandleRef, []);

    // Latest ref pattern since Wistia cleanup/re-initialization cause issues but this way
    // we don't have to rerun the useEffect on onLoad change
    const onLoadRef = React.useRef<typeof onLoad>(onLoad);
    useLayoutEffect(() => {
      onLoadRef.current = onLoad;
    }, [onLoad]);

    useEffect(() => {
      if (!document.getElementById(captionContainerID)) {
        const captionContainerHider = document.createElement("div");
        const captionContainer = document.createElement("div");
        captionContainerHider.style.position = "absolute";
        captionContainerHider.style.left = "-9999px";
        captionContainer.id = captionContainerID;
        document.body.appendChild(captionContainerHider);
        captionContainerHider.appendChild(captionContainer);
      }

      window._wq = window._wq || [];
      window._wq.push({
        id: internalVideoID,
        onReady: function (video: WistiaAPI) {
          videoHandleRef.current = video;
          setIsLoaded(true);
          onLoadRef.current?.(video);
        },
      });
      return () => {
        videoHandleRef.current?.remove?.();
        document.getElementById(captionContainerID)?.remove();
      };
    }, [internalVideoID, captionContainerID]);

    return (
      <>
        <WistiaVideoContext.Provider
          value={{
            isLoaded,
            captionContainerID,
            video: videoHandleRef.current,
          }}
        >
          {children}
        </WistiaVideoContext.Provider>
        <div style={{ width: "0px", height: "0px", overflow: "hidden" }}>
          <span
            className={classNames(
              `wistia_embed`,
              `wistia_async_${internalVideoID}`,
              `stillUrl=data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=`,
              `popover=true`,
              `popoverOverlayOpacity=0.4`,
              `popoverCaptionContainer=caption-container-${internalVideoID}`
            )}
          ></span>
        </div>
      </>
    );
  }
);

const WistiaCaption = ({ children }: { children: React.ReactNode }) => {
  const { captionContainerID, video } = useContext(WistiaVideoContext);

  const boundingBoxRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const captionHeight = boundingBoxRef.current?.getBoundingClientRect().height;
    if (captionHeight && video) {
      if (window.innerHeight < video.height() + captionHeight * 2) {
        video.height(video.height() - captionHeight * 2, { constrain: true });
      }
    }
  }, [children, video]);

  const container = document.getElementById(captionContainerID);
  if (!container) {
    return null;
  }

  return (
    <>
      {createPortal(
        <div ref={boundingBoxRef} style={{ overflow: "auto" }}>
          {children}
        </div>,
        container
      )}
    </>
  );
};

const WistiaLink = (props: {
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
}) => {
  const { isLoaded, video } = useContext(WistiaVideoContext);

  return (
    <>
      {isLoaded && (
        <Link
          onClick={(e) => {
            video?.play?.();
            props.onClick?.(e);
          }}
        >
          {props.children}
        </Link>
      )}
    </>
  );
};

const WistiaThumbnail: React.FC<React.ImgHTMLAttributes<HTMLImageElement>> = (props) => {
  const { isLoaded, video } = useContext(WistiaVideoContext);

  return (
    <div
      className={classNames(props.className, style.imageContainer)}
      onClick={(e) => {
        video?.play?.();
        props.onClick?.(e as React.MouseEvent<HTMLImageElement>);
      }}
    >
      <img style={{ width: "100%" }} src={props.src} alt={props.alt} />
      {isLoaded && (
        <>
          <div role="button" className={style.playButtonBackground} />
          <div role="button" className={style.playButton} />
        </>
      )}
    </div>
  );
};

interface IWistiaVideo
  extends React.ForwardRefExoticComponent<
    WistiaVideoProps & React.RefAttributes<React.MutableRefObject<WistiaAPI | null>>
  > {
  Thumbnail: typeof WistiaThumbnail;
  Link: typeof WistiaLink;
  Caption: typeof WistiaCaption;
}

export const WistiaVideo = {
  ...InternalWistiaVideo,
  Thumbnail: WistiaThumbnail,
  Link: WistiaLink,
  Caption: WistiaCaption,
} as IWistiaVideo;
